import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import QaRolesCustomSelectField from 'components/QaRolesCustomSelectField';
import RadioGroupField from 'components/RadioGroupField';
import useCallbackRef from 'hooks/useCallbackRef';
import { WaitingRoomRequest } from 'models/WaitingRoom';
import { normalizeEmail } from 'utils';
import SideDrawer from '../../../../components/SideDrawer';
import {
  DrawerNavigation,
  SideDrawerRef,
} from '../../../../components/SideDrawer/SideDrawer.component';
import useMembers from '../../../../subrouters/Members/hooks/useMembers';
import useOptions from '../../../../subrouters/Members/hooks/useOptions';
import { DATA_ROOM_MEMBER_ROLES } from '../../../../models/DataRoomMember';
import { Q_AND_A_ROLES } from '../../../../models/QAndA';
import { ApproveSecureLinkRequestPayload } from '../../../../dataRoomApi/waiting-room';
import useSecureLinkRequestReview from '../../hooks/useSecureLinkRequestReview';
import { SecureLinkSummary } from './components/SecureLinkSummary';
import {
  GRANT_ACCESS_VIA,
  GRANT_ACCESS_VIA_FIELD,
  GrantAccessVia,
  SECURE_LINK_REVIEW_TITLE,
  SECURE_LINK_REVIEW_VARIANTS,
} from './data';

import './ReviewSecureLinkRequest.styles.scss';

type ReviewSecureLinkRequestProps = {
  className?: string;
  request: WaitingRoomRequest;
  // Handing off to the create-secure-link drawer, which resolves the request
  // itself on submit. Every other outcome is settled in here.
  onCreateNewLink: (request: WaitingRoomRequest) => void;
} & DrawerNavigation;

// The radio only routes the seller between the two ways out of the drawer; it is
// not part of what approving sends.
type ReviewFormValues = ApproveSecureLinkRequestPayload & {
  grantAccessVia: GrantAccessVia;
};

// What a requester who is not in the room yet is let in as. Both are editable —
// this is only where the two selects open.
const DEFAULT_ROLE = DATA_ROOM_MEMBER_ROLES.PUBLISHER;
const DEFAULT_QA_ROLE = Q_AND_A_ROLES.DRAFTER;

const ReviewSecureLinkRequest: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ReviewSecureLinkRequestProps
> = ({ className, request, onCreateNewLink, onPrev, onNext }, ref) => {
  const { t } = useTranslation();

  const { _id, type, email, requestingAccessTo, dataRoomShareableLink } =
    request ?? {};

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  // Everyone who comes in through a secure link lands on the buy side.
  const { buyerRoleOptions, buyerQaRole } = useOptions();
  const { buySideMembers } = useMembers();

  // The requester may already be in the room — through another link, another
  // group or an invite — in which case the roles they already hold are what the
  // selects open on. Matching is by email: secure link requests often come from
  // someone with no account yet, so `userId` is not there to match on.
  const existingMember = useMemo(() => {
    if (!email) return undefined;

    return buySideMembers.find(
      (member) => normalizeEmail(member.email) === normalizeEmail(email),
    );
  }, [buySideMembers, email]);

  // Approving through the link the request came in on is what the drawer is for,
  // so it starts selected. Keyed on the request id as well as the member, so
  // final-form reinitialises for the next row it is opened on — the drawer stays
  // mounted between reviews, and edits made for one requester must not carry.
  const initialValues = useMemo<Partial<ReviewFormValues>>(
    () => ({
      [GRANT_ACCESS_VIA_FIELD]: GRANT_ACCESS_VIA.EXISTING_LINK,
      role: existingMember?.role ?? DEFAULT_ROLE,
      qaRole: existingMember?.qaRole ?? DEFAULT_QA_ROLE,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_id, existingMember],
  );

  const { isLoading, approveRequest, declineRequest } =
    useSecureLinkRequestReview();

  const variant = SECURE_LINK_REVIEW_VARIANTS[type];

  const classes = classNames('review-secure-link-request', className);

  const close = useCallback(() => drawer?.setOpen(false), [drawer]);

  const onSubmit = useCallback(
    async ({ role, qaRole }: ReviewFormValues) =>
      approveRequest(request, { role, qaRole }, close),
    [approveRequest, close, request],
  );

  const onDecline = useCallback(
    async () => declineRequest(_id, close),
    [_id, close, declineRequest],
  );

  // Creating a link instead is a hand-off, not an outcome: the create drawer
  // carries the request id and resolves it on submit. Selecting the option is
  // the whole interaction, so it reverts once the other drawer has it.
  const onGrantAccessViaChange = useCallback(
    (value: GrantAccessVia, reset: () => void) => {
      if (value !== GRANT_ACCESS_VIA.NEW_LINK) return;

      reset();
      drawer?.setOpen(false);

      onCreateNewLink(request);
    },
    [drawer, onCreateNewLink, request],
  );

  const grantAccessOptions = useMemo(
    () => [
      {
        value: GRANT_ACCESS_VIA.EXISTING_LINK,
        label: (
          <span className="review-secure-link-request__option">
            <strong>{t('existingSecureLink')}</strong>

            <SecureLinkSummary
              link={dataRoomShareableLink}
              name={requestingAccessTo}
              isVisible={drawer?.open}
            />
          </span>
        ),
      },
      {
        value: GRANT_ACCESS_VIA.NEW_LINK,
        label: (
          <span className="review-secure-link-request__option">
            <strong>{t('createANewSecureLink')}</strong>

            <span className="review-secure-link-request__option__note">
              {t('createANewSecureLinkDescription')}
            </span>
          </span>
        ),
      },
    ],
    [dataRoomShareableLink, drawer?.open, requestingAccessTo, t],
  );

  // The drawer itself always mounts, even before a request is picked: the parent
  // opens it through this ref, and a ref on something conditionally rendered is
  // not attached yet on the click that would set the request.
  return (
    <SideDrawer
      ref={drawerRef}
      title={t(SECURE_LINK_REVIEW_TITLE)}
      className={classes}
      onPrev={onPrev}
      onNext={onNext}
    >
      {!!variant && (
        <>
          <dl className="review-secure-link-request__details">
            <dt>{t('email')}</dt>
            <dd>{normalizeEmail(email)}</dd>

            <dt>{t(variant.targetLabel)}</dt>
            <dd>{requestingAccessTo}</dd>
          </dl>

          <Form<ReviewFormValues>
            initialValues={initialValues}
            onSubmit={onSubmit}
            render={({ handleSubmit, form, invalid }) => (
              <form onSubmit={handleSubmit}>
                {isLoading && <Loader />}

                {variant.hasRoles && (
                  <section className="review-secure-link-request__section">
                    <h3>{t('access')}</h3>

                    <div className="review-secure-link-request__section__roles">
                      <QaRolesCustomSelectField
                        name="role"
                        label={t('filesRole')}
                        options={buyerRoleOptions}
                        renderAsPortal
                      />

                      <QaRolesCustomSelectField
                        name="qaRole"
                        label={t('qaRole')}
                        options={buyerQaRole}
                        renderAsPortal
                      />
                    </div>
                  </section>
                )}

                <section className="review-secure-link-request__section">
                  <h3>{t('grantAccessVia')}</h3>

                  {/* Deliberately not `styled`: that variant hides the radio
                  itself and shows selection through the card border alone. */}
                  <RadioGroupField<GrantAccessVia>
                    name={GRANT_ACCESS_VIA_FIELD}
                    options={grantAccessOptions}
                    direction="column"
                    noShowErr
                    customOnChange={(value) =>
                      onGrantAccessViaChange(value, () =>
                        form.change(
                          GRANT_ACCESS_VIA_FIELD,
                          GRANT_ACCESS_VIA.EXISTING_LINK,
                        ),
                      )
                    }
                  />
                </section>

                <p className="review-secure-link-request__footnote">
                  {t(variant.footnote)}
                </p>

                <div className="review-secure-link-request__actions">
                  <Button
                    type="button"
                    variant="link"
                    disabled={isLoading}
                    onClick={onDecline}
                  >
                    {t('decline')}
                  </Button>

                  <Button type="submit" disabled={isLoading || invalid}>
                    {t(variant.submitLabel)}
                  </Button>
                </div>
              </form>
            )}
          />
        </>
      )}
    </SideDrawer>
  );
};

export default forwardRef(ReviewSecureLinkRequest);
